import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { API } from "@/App";
import { 
  Bell, 
  Check, 
  CheckCheck,
  Trash2,
  CreditCard,
  Users,
  UserPlus,
  GraduationCap,
  Shield,
  School,
  Info,
  AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

const NOTIFICATION_ICONS = {
  billing_reminder_30: CreditCard,
  billing_reminder_15: CreditCard,
  billing_reminder_7: CreditCard,
  billing_overdue: AlertCircle,
  payment_success: CreditCard,
  payment_failed: AlertCircle,
  subscription_activated: CreditCard,
  subscription_cancelled: CreditCard,
  student_enrolled: UserPlus,
  student_bulk_upload: Users,
  student_status_change: Users,
  student_graduated: GraduationCap,
  admin_added: Shield,
  admin_removed: Shield,
  admin_permission_change: Shield,
  teacher_added: GraduationCap,
  teacher_removed: GraduationCap,
  school_verified: School,
  school_settings_updated: School,
  system_announcement: Info,
  maintenance_scheduled: AlertCircle
};

const PRIORITY_COLORS = {
  low: "bg-slate-100 text-slate-600",
  medium: "bg-blue-100 text-blue-600",
  high: "bg-amber-100 text-amber-600",
  urgent: "bg-red-100 text-red-600"
};

const PRIORITY_DOT_COLORS = {
  low: "bg-slate-400",
  medium: "bg-blue-500",
  high: "bg-amber-500",
  urgent: "bg-red-500"
};

export default function NotificationBell({ onLoginNotifications }) {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const hasShownLoginToast = useRef(false);

  useEffect(() => {
    fetchNotifications();
    
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Show toast for unread notifications on login
  useEffect(() => {
    if (unreadCount > 0 && !hasShownLoginToast.current && onLoginNotifications) {
      hasShownLoginToast.current = true;
      
      // Find highest priority unread notification
      const unreadNotifs = notifications.filter(n => !n.is_read);
      const urgentNotif = unreadNotifs.find(n => n.priority === "urgent");
      const highNotif = unreadNotifs.find(n => n.priority === "high");
      const topNotif = urgentNotif || highNotif || unreadNotifs[0];
      
      if (topNotif) {
        toast(topNotif.title, {
          description: topNotif.message,
          action: topNotif.action_url ? {
            label: "View",
            onClick: () => window.location.href = topNotif.action_url
          } : undefined,
          duration: 5000
        });
      }
      
      if (unreadCount > 1) {
        setTimeout(() => {
          toast.info(`You have ${unreadCount} unread notifications`, {
            duration: 3000
          });
        }, 1500);
      }
    }
  }, [unreadCount, notifications, onLoginNotifications]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/school/notifications/list?limit=20`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const response = await fetch(`${API}/school/notifications/mark-read`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ notification_id: notificationId }),
      });

      if (response.ok) {
        setNotifications(prev => 
          prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await fetch(`${API}/school/notifications/mark-all-read`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);
        toast.success("All notifications marked as read");
      }
    } catch (error) {
      toast.error("Failed to mark notifications as read");
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const response = await fetch(`${API}/school/notifications/${notificationId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        const wasUnread = notifications.find(n => n.id === notificationId && !n.is_read);
        if (wasUnread) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      toast.error("Failed to delete notification");
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
            <h3 className="font-semibold text-slate-800">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-emerald-600 hover:text-emerald-700"
                onClick={markAllAsRead}
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notifications List */}
          <ScrollArea className="max-h-[400px]">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Bell className="h-10 w-10 mb-2 opacity-50" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <div>
                {notifications.map((notification) => {
                  const Icon = NOTIFICATION_ICONS[notification.notification_type] || Info;
                  const priorityColor = PRIORITY_COLORS[notification.priority] || PRIORITY_COLORS.medium;
                  const dotColor = PRIORITY_DOT_COLORS[notification.priority] || PRIORITY_DOT_COLORS.medium;

                  const content = (
                    <div
                      key={notification.id}
                      className={`
                        relative px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors
                        ${!notification.is_read ? "bg-emerald-50/50" : ""}
                      `}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      {/* Unread indicator */}
                      {!notification.is_read && (
                        <div className={`absolute left-2 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${dotColor}`} />
                      )}

                      <div className="flex items-start gap-3 pl-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${priorityColor}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm font-medium ${!notification.is_read ? "text-slate-900" : "text-slate-700"}`}>
                              {notification.title}
                            </p>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              {!notification.is_read && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markAsRead(notification.id);
                                  }}
                                >
                                  <Check className="h-3 w-3" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-slate-400 hover:text-red-500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteNotification(notification.id);
                                }}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-1">
                            {formatTime(notification.created_at)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );

                  if (notification.action_url) {
                    return (
                      <Link key={notification.id} to={notification.action_url}>
                        {content}
                      </Link>
                    );
                  }
                  return content;
                })}
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-100 bg-slate-50">
              <Button
                variant="ghost"
                className="w-full text-sm text-slate-600 hover:text-emerald-600"
                onClick={() => {
                  setIsOpen(false);
                  fetchNotifications();
                }}
              >
                Refresh
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
